import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Chip,
  Divider,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import axios from 'axios';

const ShopAuthManagement = ({path}) => {
  const [users, setUsers] = useState([]);
  const [shops, setShops] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchShop, setSearchShop] = useState('');
  const [unsavedChanges, setUnsavedChanges] = useState(false);


  useEffect(() => {
    fetchUsers();
    fetchShops();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchUserPermissions(selectedUser);
    }
  }, [selectedUser]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(path + '/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Kullanıcılar yüklenemedi', 'error');
    }
  };

  const fetchShops = async () => {
    try {
      const response = await axios.get(path + '/api/shops');
      setShops(response.data);
    } catch (error) {
      console.error('Error fetching shops:', error);
      showSnackbar('Shoplar yüklenemedi', 'error');
    }
  };

  const fetchUserPermissions = async (userId) => {
    setLoading(true);
    try {
      const response = await axios.get(path + `/api/userShopAuth/${userId}`);
      const permMap = {};
      response.data.forEach(perm => {
        permMap[perm.ShopNodeID] = {
          CanView: perm.CanView === 1,
          CanEdit: perm.CanEdit === 1
        };
      });
      setPermissions(permMap);
      setUnsavedChanges(false);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      showSnackbar('Yetkiler yüklenemedi', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (shopId, type, value) => {
    setPermissions(prev => ({
      ...prev,
      [shopId]: {
        ...prev[shopId],
        [type]: value,
        // Eğer Edit yetkisi veriliyorsa, View otomatik açılmalı
        ...(type === 'CanEdit' && value ? { CanView: true } : {}),
        // Eğer View yetkisi kapatılıyorsa, Edit de kapatılmalı
        ...(type === 'CanView' && !value ? { CanEdit: false } : {})
      }
    }));
    setUnsavedChanges(true);
  };

  const handleSaveAll = async () => {
    if (!selectedUser) {
      showSnackbar('Lütfen bir kullanıcı seçin', 'warning');
      return;
    }

    setLoading(true);
    try {
      // Sadece yetkisi olan shopları gönder
      const permissionsToSave = Object.entries(permissions)
        .filter(([_, perm]) => perm.CanView || perm.CanEdit)
        .map(([shopId, perm]) => ({
          ShopNodeID: parseInt(shopId),
          CanView: perm.CanView ? 1 : 0,
          CanEdit: perm.CanEdit ? 1 : 0
        }));

      await axios.post(path + '/api/userShopAuth/bulk', {
        UserID: selectedUser,
        permissions: permissionsToSave
      });

      showSnackbar('Yetkiler başarıyla kaydedildi', 'success');
      setUnsavedChanges(false);
      fetchUserPermissions(selectedUser);
    } catch (error) {
      console.error('Error saving permissions:', error);
      showSnackbar('Kaydetme hatası', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (value) => {
    const newPermissions = {};
    shops.forEach(shop => {
      newPermissions[shop.id] = {
        CanView: value,
        CanEdit: false
      };
    });
    setPermissions(newPermissions);
    setUnsavedChanges(true);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getPermissionStats = () => {
    const total = shops.length;
    const withView = Object.values(permissions).filter(p => p?.CanView).length;
    const withEdit = Object.values(permissions).filter(p => p?.CanEdit).length;
    return { total, withView, withEdit };
  };

  const filteredShops = shops.filter(shop =>
    shop.name?.toLowerCase().includes(searchShop.toLowerCase()) ||
    shop.buttonName?.toLowerCase().includes(searchShop.toLowerCase())
  );

  const stats = getPermissionStats();
  const selectedUserName = users.find(u => u.UserID === selectedUser)?.Username;

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StoreIcon color="primary" fontSize="large" />
          Shop Yetkilendirme Yönetimi
        </Typography>
        
        <Divider sx={{ my: 2 }} />

        {/* Kullanıcı Seçimi */}
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Kullanıcı Seçin</InputLabel>
              <Select
                value={selectedUser}
                label="Kullanıcı Seçin"
                onChange={(e) => setSelectedUser(e.target.value)}
                startAdornment={<PersonIcon sx={{ mr: 1, color: 'action.active' }} />}
              >
                <MenuItem value="">Seçiniz...</MenuItem>
                {users.map(user => (
                  <MenuItem key={user.UserID} value={user.UserID}>
                    {user.Username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {selectedUser && (
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip 
                  icon={<CheckCircleIcon />} 
                  label={`Görüntüleme: ${stats.withView}/${stats.total}`} 
                  color="primary" 
                  variant="outlined"
                />
                <Chip 
                  icon={<CheckCircleIcon />} 
                  label={`Düzenleme: ${stats.withEdit}/${stats.total}`} 
                  color="secondary" 
                  variant="outlined"
                />
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Toplu İşlemler */}
        {selectedUser && (
          <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleSelectAll(true)}
            >
              Tümüne Görüntüleme Ver
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={() => handleSelectAll(false)}
            >
              Tümünü Temizle
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveAll}
              disabled={loading || !unsavedChanges}
              color="success"
            >
              {loading ? <CircularProgress size={24} /> : 'Değişiklikleri Kaydet'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Shop Listesi */}
      {selectedUser && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Shop ara..."
              value={searchShop}
              onChange={(e) => setSearchShop(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredShops.map(shop => {
                const perm = permissions[shop.id] || { CanView: false, CanEdit: false };
                return (
                  <Grid item xs={12} sm={6} md={4} key={shop.id}>
                    <Card 
                      elevation={perm.CanView ? 3 : 1}
                      sx={{ 
                        border: perm.CanView ? '2px solid' : '1px solid',
                        borderColor: perm.CanEdit ? 'secondary.main' : perm.CanView ? 'primary.main' : 'divider',
                        transition: 'all 0.3s'
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6" gutterBottom noWrap>
                          {shop.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
                          {shop.buttonName}
                        </Typography>
                        
                        <Divider sx={{ my: 1.5 }} />
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={perm.CanView || false}
                                onChange={(e) => handlePermissionChange(shop.id, 'CanView', e.target.checked)}
                                color="primary"
                              />
                            }
                            label="Görüntüleme"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={perm.CanEdit || false}
                                onChange={(e) => handlePermissionChange(shop.id, 'CanEdit', e.target.checked)}
                                disabled={!perm.CanView}
                                color="secondary"
                              />
                            }
                            label="Düzenleme"
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {filteredShops.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <CancelIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Shop bulunamadı
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Kaydedilmemiş değişiklikler uyarısı */}
      {unsavedChanges && (
        <Paper 
          elevation={6}
          sx={{ 
            position: 'fixed', 
            bottom: 20, 
            left: '50%', 
            transform: 'translateX(-50%)',
            p: 2,
            bgcolor: 'warning.main',
            color: 'white',
            zIndex: 9999
          }}
        >
          <Typography>
            ⚠️ Kaydedilmemiş değişiklikleriniz var! Değişiklikleri kaydetmeyi unutmayın.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ShopAuthManagement;